/**
 * Live event notification preferences (2026-10-30 owner ask).
 *
 * "Remind me" was one tap per event. A trainer now sets it up ONCE, from the
 * intro sheet (components/LiveEventsIntroSheet) or Settings: which event
 * TYPES get an automatic countdown, how long before the start it appears,
 * and whether it is the countdown card (Lock Screen / Dynamic Island on
 * iPhone, an ongoing notification on Android) or a plain notification.
 *
 * The choice lives on the ACCOUNT (Worker migration 0110, GET/PUT
 * /api/events/live-prefs): the iPhone card is started by the Worker's push,
 * so the Worker must know. This module keeps a copy on the device so every
 * card can say "Countdown set" without a round trip, and so the web / a
 * signed-out session still has a working choice.
 *
 * The type slugs mirror cloudflare/src/lib/liveEventPrefs.ts; keep the two
 * lists identical.
 */
import { useSyncExternalStore } from "react";
import { eventCountdownDate } from "./calendarLinks";
import type { GameEvent } from "./data";
import { apiRequest, hasAuthToken } from "./queryClient";

export type LiveEventType = GameEvent["category"];

export const LIVE_EVENT_TYPES: readonly LiveEventType[] = [
  "community-day",
  "raid-day",
  "raid-hour",
  "raid",
  "spotlight",
  "go-fest",
  "hatch-day",
  "max-battle",
  "max-monday",
  "research",
  "go-pass",
  "season",
  "league",
  "pvp",
  "event",
];

/**
 * Ticked on a fresh sheet (owner direction 2026-10-30): the time-boxed
 * kinds. Season, GO Pass, League and PvP run for weeks, so a countdown adds
 * little; they stay on the list, unticked.
 */
export const DEFAULT_LIVE_EVENT_TYPES: readonly LiveEventType[] = [
  "community-day",
  "raid-day",
  "raid-hour",
  "raid",
  "spotlight",
  "go-fest",
  "hatch-day",
  "max-battle",
  "max-monday",
  "research",
  "event",
];

/**
 * The longest event that gets a live card (owner direction 2026-10-31): a
 * Raid Hour, a Spotlight Hour, a three-hour Community Day, a Max Monday
 * evening, or a two-day GO Fest / GO Tour / Wild Area weekend. A week-long
 * raid rotation, a Timed Research running for months, a GO Pass or a
 * Season has no moment to count down to, so it is never a live card
 * whatever is ticked; Remind me on one is a plain notification. Mirrors
 * cloudflare/src/lib/liveActivities.ts; change both.
 */
export const LIVE_EVENT_MAX_WINDOW_MS = 48 * 60 * 60_000;

/**
 * Short enough for a card? The wall-clock length of start to end against
 * the cap. No end, or one that cannot be read, counts as a moment.
 */
export function isTimeBoxedEvent(ev: { start?: string | null; end?: string | null }): boolean {
  if (!ev.start || !ev.end) return true;
  const startAt = eventCountdownDate(ev.start).getTime();
  const endAt = eventCountdownDate(ev.end).getTime();
  if (Number.isNaN(startAt) || Number.isNaN(endAt)) return true;
  return endAt - startAt <= LIVE_EVENT_MAX_WINDOW_MS;
}

export type LiveEventLeadMinutes = 60 | 30 | 15 | 0;
export const LEAD_OPTIONS: ReadonlyArray<{ minutes: LiveEventLeadMinutes; label: string; short: string }> = [
  { minutes: 60, label: "1 hour before", short: "1 hr" },
  { minutes: 30, label: "30 minutes before", short: "30 min" },
  { minutes: 15, label: "15 minutes before", short: "15 min" },
  { minutes: 0, label: "Only once it is on", short: "At start" },
];

/** "live": the countdown card. "push": a notification only. */
export type LiveEventDelivery = "live" | "push";

export interface LiveEventPrefs {
  enabled: boolean;
  types: LiveEventType[];
  leadMinutes: LiveEventLeadMinutes;
  delivery: LiveEventDelivery;
}

export function isLiveEventType(v: unknown): v is LiveEventType {
  return typeof v === "string" && (LIVE_EVENT_TYPES as readonly string[]).includes(v);
}

export function isLeadMinutes(v: unknown): v is LiveEventLeadMinutes {
  return v === 60 || v === 30 || v === 15 || v === 0;
}

/** The defaults, plus every type the trainer has tapped "Remind me" on. */
export function suggestedPrefs(remindedTypes: readonly string[] = []): LiveEventPrefs {
  const types = [...DEFAULT_LIVE_EVENT_TYPES];
  for (const t of remindedTypes) if (isLiveEventType(t) && !types.includes(t)) types.push(t);
  return { enabled: true, types, leadMinutes: 60, delivery: "live" };
}

/** "1 hour", "30 minutes", "15 minutes", or "" for no countdown. */
export function leadLabel(minutes: LiveEventLeadMinutes): string {
  if (minutes === 0) return "";
  if (minutes === 60) return "1 hour";
  return `${minutes} minutes`;
}

/**
 * Does the standing choice cover this event? Switched on, its type ticked,
 * short enough for a card (isTimeBoxedEvent), and, for a one-place event
 * (GO Tour in a city), only when the trainer is attending: the Worker
 * applies the same rules to the card it starts.
 */
export function coversEvent(
  prefs: LiveEventPrefs | null | undefined,
  ev: { category: GameEvent["category"]; geoLocked?: boolean; start?: string | null; end?: string | null },
  attending: boolean = false,
): boolean {
  if (!prefs || !prefs.enabled) return false;
  if (!prefs.types.includes(ev.category)) return false;
  if (!isTimeBoxedEvent(ev)) return false;
  if (ev.geoLocked && !attending) return false;
  return true;
}

function sanitize(raw: unknown): LiveEventPrefs | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as Partial<Record<keyof LiveEventPrefs, unknown>>;
  if (typeof p.enabled !== "boolean" || !Array.isArray(p.types)) return null;
  const types: LiveEventType[] = [];
  for (const t of p.types) if (isLiveEventType(t) && !types.includes(t)) types.push(t);
  return {
    enabled: p.enabled,
    types,
    leadMinutes: isLeadMinutes(p.leadMinutes) ? p.leadMinutes : 60,
    delivery: p.delivery === "push" ? "push" : "live",
  };
}

// ── The device's copy ────────────────────────────────────────────────────────
const STORAGE_KEY = "nexus.liveEventPrefs.v1";
export const LIVE_EVENT_PREFS_EVENT = "nexus:live-event-prefs-changed";

let cache: LiveEventPrefs | null | undefined;

function read(): LiveEventPrefs | null {
  if (cache !== undefined) return cache;
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(STORAGE_KEY);
    cache = raw ? sanitize(JSON.parse(raw) as unknown) : null;
  } catch {
    cache = null;
  }
  return cache;
}

function writeLocal(prefs: LiveEventPrefs | null): void {
  cache = prefs;
  try {
    if (prefs) localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode: the session still knows */
  }
  try {
    window.dispatchEvent(new Event(LIVE_EVENT_PREFS_EVENT));
  } catch {
    /* no window */
  }
}

/** The standing choice on this device, or null when none has been made. */
export function getLiveEventPrefs(): LiveEventPrefs | null {
  return read();
}

function subscribe(cb: () => void): () => void {
  window.addEventListener(LIVE_EVENT_PREFS_EVENT, cb);
  window.addEventListener("storage", cb);
  return () => {
    window.removeEventListener(LIVE_EVENT_PREFS_EVENT, cb);
    window.removeEventListener("storage", cb);
  };
}

export function useLiveEventPrefs(): LiveEventPrefs | null {
  return useSyncExternalStore(subscribe, read, () => null);
}

// ── The account's copy ───────────────────────────────────────────────────────
export interface LiveEventPrefsResponse {
  prefs: LiveEventPrefs | null;
  /** Types of the events the account tapped "Remind me" on (for the sheet's pre-ticks). */
  remindedTypes: string[];
}

/**
 * The account's choice, which replaces the device's copy (a Settings change
 * on another phone wins). Signed out: the device's copy, no reminded types.
 */
export async function fetchLiveEventPrefs(): Promise<LiveEventPrefsResponse> {
  if (!hasAuthToken()) return { prefs: read(), remindedTypes: [] };
  try {
    const res = await apiRequest("GET", "/api/events/live-prefs");
    const body = (await res.json()) as { prefs?: unknown; remindedTypes?: unknown };
    const prefs = sanitize(body.prefs);
    if (prefs) writeLocal(prefs);
    const remindedTypes = Array.isArray(body.remindedTypes) ? body.remindedTypes.filter((t): t is string => typeof t === "string") : [];
    return { prefs, remindedTypes };
  } catch {
    return { prefs: read(), remindedTypes: [] };
  }
}

/**
 * Save the choice: on the account when signed in (the Worker starts the
 * iPhone card from it), and always on this device. Resolves false when the
 * account copy could not be written; the device copy still stands.
 */
export async function saveLiveEventPrefs(prefs: LiveEventPrefs): Promise<boolean> {
  const clean = sanitize(prefs) ?? suggestedPrefs();
  writeLocal(clean);
  if (!hasAuthToken()) return true;
  try {
    await apiRequest("PUT", "/api/events/live-prefs", clean);
    return true;
  } catch (err) {
    console.warn("[liveEventPrefs] save failed", err);
    return false;
  }
}

/** Tests only. */
export function __resetLiveEventPrefsForTest(): void {
  cache = undefined;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
